import { useMemo } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import { InfoHint } from "@/components/ui/info-hint";
import { useNetCommissionKpis } from "@/hooks/useInvoices";
import { useGoals, useUpsertPersonalGoal, useUpsertTeamGoal } from "@/hooks/useGoals";
import { GoalEditDialog } from "@/components/goals/GoalEditDialog";
import { SectionLoadError } from "@/components/invoicing/SectionErrorBoundary";
import { useQueryClient } from "@tanstack/react-query";
import { formatKpiCurrency } from "@/lib/formats/currency";
import { computeForecast } from "@/lib/goalForecast";

interface NetCommissionCardProps {
  tramsAccountId?: number;
  viewMode: "my" | "all";
  returnDateFrom?: string;
  returnDateTo?: string;
}

/**
 * YTD Net Paid Commission KPI card (Agency Owners only, per Updated_Metrics.xlsx).
 * Team view = agency total; personal view = the owner's share.
 *
 * DATA SOURCE: `GET /api/invoices/net-commission-kpis` sums the owner's own
 * statements by statement week — the same log as the GEM card, so Net cannot
 * run ahead of Gross between invoice close and statement ingest (AR-456).
 * Personal view counts only the lines the owner sold; team view adds the
 * owner's share of associates' bookings.
 *
 * GOAL (AR-314): the owner's "Year Net Commission Goal" (team goal in team
 * view, personal goal in personal view) with the GEM card's On/Off Track
 * projection. The endpoint has no outstanding net figure, so the projection
 * extrapolates paid net only.
 */
export function NetCommissionCard({
  tramsAccountId,
  viewMode,
  returnDateFrom,
  returnDateTo,
}: NetCommissionCardProps) {
  const queryClient = useQueryClient();
  const currentYear = new Date().getFullYear();
  const {
    data,
    isLoading: kpiLoading,
    error: kpiError,
  } = useNetCommissionKpis({
    tramsAccountId,
    viewMode,
    returnDateFrom,
    returnDateTo,
  });
  const {
    data: goalsData,
    isLoading: goalsLoading,
    error: goalsError,
  } = useGoals(currentYear);

  const upsertPersonal = useUpsertPersonalGoal();
  const upsertTeam = useUpsertTeamGoal();

  const isTeamView = viewMode === "all";
  const hasReturnDateFilter = Boolean(
    returnDateFrom?.trim() || returnDateTo?.trim(),
  );
  const label = hasReturnDateFilter
    ? "Net Paid Commission"
    : "YTD Net Paid Commission";

  const hasTeamAccess = goalsData?.showTeamByDefault ?? false;
  const canEditTeamGoals = goalsData?.canEditTeamGoals ?? false;
  const canEditNetGoal = goalsData?.canEditNetGoal ?? false;

  const goalScope: "team" | "personal" =
    isTeamView && (hasTeamAccess || canEditTeamGoals) ? "team" : "personal";
  const activeGoal = goalScope === "team" ? goalsData?.team : goalsData?.personal;
  const goalAmount = parseFloat(activeGoal?.netCommissionGoal ?? "0") || 0;
  const netAmount = parseFloat(data?.netCommissionYtd ?? "0") || 0;

  const forecast = useMemo(
    () => computeForecast(netAmount, 0, goalAmount, !hasReturnDateFilter),
    [netAmount, goalAmount, hasReturnDateFilter],
  );

  const canEdit =
    canEditNetGoal && (goalScope === "personal" || canEditTeamGoals);

  // A goals failure only affects the footer; the net figure still renders.
  if (kpiError) {
    return (
      <SectionLoadError
        sectionName={label}
        error={kpiError}
        onRetry={() => {
          queryClient.invalidateQueries({
            queryKey: ["/api/invoices/net-commission-kpis"],
          });
          queryClient.invalidateQueries({ queryKey: ["/api/goals"] });
        }}
      />
    );
  }

  if (kpiLoading || goalsLoading) {
    return (
      <div className="rounded-xl border bg-card p-5">
        <Skeleton className="h-4 w-44 mb-3" />
        <Skeleton className="h-10 w-44 mb-1" />
        <Skeleton className="h-3 w-28 mb-3" />
        <Skeleton className="h-6 w-full rounded-lg" />
      </div>
    );
  }

  const goalEditDialog = canEdit && (
    <GoalEditDialog
      title={goalScope === "team" ? "Team Goals" : "Personal Goals"}
      description={`Set ${goalScope} targets for ${currentYear}.`}
      goal={activeGoal ?? null}
      year={currentYear}
      onSave={async (p) => {
        if (goalScope === "team") {
          await upsertTeam.mutateAsync(p);
        } else {
          await upsertPersonal.mutateAsync(p);
        }
      }}
      isSaving={upsertPersonal.isPending || upsertTeam.isPending}
      showNetCommissionGoal
    />
  );

  return (
    <div className="rounded-xl border bg-card overflow-hidden flex flex-col">
      <div className="p-5 pb-4">
        <div className="flex items-center gap-1.5 mb-1">
          <p className="text-sm font-medium uppercase tracking-wide text-muted-foreground">
            {label}
          </p>
          <InfoHint label={`How ${label.toLowerCase()} is calculated`}>
            <p>
              {hasReturnDateFilter
                ? "Net commission from closed invoices whose trip return date falls in the selected range, paid by statement date."
                : "Net commission paid out this calendar year by statement date from closed invoices."}{" "}
              Team view shows your agency total, personal view your share only.
              Net means after commission split, not gross.
            </p>
          </InfoHint>
        </div>
        <p className="text-4xl font-bold tracking-tight text-foreground">
          {formatKpiCurrency(netAmount)}
        </p>
        {goalAmount > 0 && (
          <p className="text-lg text-muted-foreground mt-0.5">
            /{formatKpiCurrency(goalAmount)}
          </p>
        )}
        <p className="text-sm text-muted-foreground mt-0.5">
          {isTeamView ? "Paid to your agency" : "Paid to you"}
        </p>
      </div>

      {goalAmount > 0 ? (
        <div className="bg-muted/40 px-5 py-3.5 flex items-start justify-between gap-2 mt-auto border-t border-border/50">
          <div className="min-w-0 flex-1">
            <p
              className={`text-sm font-semibold ${
                forecast.onTrack
                  ? "text-green-700 dark:text-green-400"
                  : "text-red-600 dark:text-red-400"
              }`}
            >
              {forecast.onTrack ? "On Track" : "Off Track"}
            </p>
            <p className="text-sm text-muted-foreground break-words">
              {forecast.label}
            </p>
          </div>
          {goalEditDialog}
        </div>
      ) : (
        <div className="bg-muted/40 px-5 py-3.5 flex items-center justify-between gap-2 mt-auto border-t border-border/50">
          <p className="text-sm text-muted-foreground">
            {goalsError
              ? "Net commission goal couldn't load"
              : "No net commission goal set"}
          </p>
          {goalEditDialog}
        </div>
      )}
    </div>
  );
}
